import React, { useMemo } from 'react';
import { Phone, Users } from 'lucide-react';
import { MetricCard, type MetricCardTheme } from 'common/components';
import type { ManagerAgentAnalyticsItem } from 'services/api/getManagerAgentAnalytics';
import {
    getAgentsWithCallsCount,
    getOnlineAgentsCount,
    getOnlineAgentsStatusBreakdown,
    getTransferredCallsTotal,
} from './PerformanceAnalyticsTab.utils';

import styles from './PerformanceSummaryMetrics.module.scss';

const CALLS_THEME: MetricCardTheme = {
    icon: Phone,
    color: 'var(--slai-ui-primary-purple-dark)',
    softBackground: 'var(--slai-ui-primary-purple-light)',
};

const ONLINE_AGENTS_THEME: MetricCardTheme = {
    icon: Users,
    color: 'var(--slai-ui-success-color)',
    softBackground: 'color-mix(in srgb, var(--slai-ui-success-color) 18%, transparent)',
};

export type PerformanceSummaryMetricsProps = {
    agents: Array<Pick<ManagerAgentAnalyticsItem, 'status' | 'agentPerformance'>>;
};

export const PerformanceSummaryMetrics: React.FC<PerformanceSummaryMetricsProps> = ({ agents }) => {
    const totalCalls = useMemo(() => getTransferredCallsTotal(agents), [agents]);
    const onlineAgents = useMemo(() => getOnlineAgentsCount(agents), [agents]);
    const agentsWithCalls = useMemo(() => getAgentsWithCallsCount(agents), [agents]);
    const onlineBreakdown = useMemo(() => getOnlineAgentsStatusBreakdown(agents), [agents]);
    const totalCallsSubtitle = `across ${agentsWithCalls} ${agentsWithCalls === 1 ? 'agent' : 'agents'}`;
    const onlineAgentsSubtitle = `${onlineBreakdown.available} available · ${onlineBreakdown.onCall} on call`;

    return (
        <section
            className={styles.section}
            data-testid='performance-summary-metrics'
            aria-label='Performance summary metrics'
        >
            <div className={styles.grid}>
                <MetricCard
                    id='total-calls'
                    label='Total calls'
                    tooltip='Calls transferred to agents today (sum of transferred calls across all agents).'
                    theme={CALLS_THEME}
                    count={totalCalls}
                    subtitle={totalCallsSubtitle}
                    showPercent={false}
                />
                <MetricCard
                    id='online-agents'
                    label='Online agents'
                    tooltip='Agents currently online or on a call.'
                    theme={ONLINE_AGENTS_THEME}
                    count={onlineAgents}
                    subtitle={onlineAgentsSubtitle}
                    showPercent={false}
                />
            </div>
        </section>
    );
};

export default PerformanceSummaryMetrics;
